import Loader from "./Display/Loader";
import Stack from "@mui/material/Stack";
import { CardTitle } from "./theme/texts";
import { Theme } from "../data/style/Theme";
import { useEffect, useState } from "react";
import NoDataDiv from "./Display/NoDataDiv";
import { useNavigate } from "react-router-dom";
import * as routes from "../data/links/routes";
import { useAuth } from "../context/AuthContext";
import { errorTexts } from "../data/assets/texts";
import useScreenSize from "../context/ScreenContext";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { useNotifications } from "../context/NotificationContext";
import { CenteredRow, MainText, SpacedRow } from "../data/style/Style";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import DemandingExerciseChart from "./charts/DemandingExerciseChart";
import {
  getWorkoutStats,
  getWorkoutLeaderboard,
  getUsersPbWorkoutScore,
} from "../api/workouts";

const StatisticsRow = ({ label, value, isLast }) => {
  return (
    <SpacedRow
      sx={{
        borderBottom: isLast ? 0 : 1,
        borderColor: Theme.palette.border,
      }}
      pb={1}
      pt={1}
      gap={1}
    >
      <MainText>{label}:</MainText>
      <MainText fontWeight={700}>{value}</MainText>
    </SpacedRow>
  );
};

const WorkoutStatistics = ({ workoutId }) => {
  const { mobileDevice } = useScreenSize();
  const client = useQueryClient();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [workout, setWorkout] = useState([]);
  const [userScore, setUserScore] = useState([]);
  const { enableNotification } = useNotifications();
  const [leaderBoardPlace, setLeaderBoardPlace] = useState(0);
  const [isWorkoutPublic, setIsWorkoutPublic] = useState(false);
  const queryStatus = client.getQueryState(["workout", parseInt(workoutId)]);
  const unit =
    workout?.type === 1 || (workout?.type === 4 && workout.rest === 0)
      ? "min"
      : "reps";

  useEffect(() => {
    if (queryStatus.status === "success") {
      setWorkout(client.getQueryData(["workout", parseInt(workoutId)]));
    }
  }, [queryStatus]);

  const {
    data: statistics,
    isPending: statsPending,
    refetch: getStatistics,
  } = useQuery({
    queryFn: ({ signal }) => getWorkoutStats({ workoutId, signal }),
    queryKey: ["workout", workoutId, "statistics"],
    onError: (error) => console.log(error),
  });

  const StandingsQuery = useQuery({
    queryFn: ({ signal }) =>
      getWorkoutLeaderboard({ workoutId, gender: profile.gender, signal }),
    queryKey: ["workout", workoutId, "leaderboard", profile.gender],
    retry: false,
    throwOnError: (error) => {
      console.log(error);
    },
  });

  useEffect(() => {
    if (StandingsQuery.isFetched) {
      if (StandingsQuery.data) {
        StandingsQuery.data.map(
          (item, index) =>
            item.user === user.id && setLeaderBoardPlace(index + 1),
        );
      }
    }
  }, [StandingsQuery.isFetched, StandingsQuery.isFetching]);

  let handleErrors = (error) => {
    if (error.response.status === 400) {
      enableNotification(error.response.data.message, "error");
      return;
    }

    if (error.response.status === 403) {
      navigate(routes.LOGIN);
      return;
    }
    if (error.response.status >= 500)
      enableNotification(errorTexts.system_error, "error");
  };

  useEffect(() => {
    setIsWorkoutPublic(workout.isPublic || workout.isWeekly);
    getStatistics();
    if (workout.type)
      userBestScoreMutation.mutate({ wodId: workoutId, type_id: workout.type });
  }, [workout.type, workout.isPublic, workout.isWeekly]);

  const userBestScoreMutation = useMutation({
    mutationFn: getUsersPbWorkoutScore,
    onSuccess: (response) => setUserScore(response.data),
    onError: (error) => handleErrors(error),
  });

  return (
    <Stack justifyContent={"space-between"}>
      {workout?.type !== 3 ? (
        <CardTitle subTitle={"Analytics"} mainTitle={"Statistics"} />
      ) : (
        <CardTitle
          subTitle={"EMOM analytics"}
          mainTitle={"Most demanding exercise"}
        />
      )}
      <Stack
        height={"32vh"}
        minHeight={"200px"}
        p={1}
        sx={{
          background: Theme.palette.card,
          borderRadius: 4,
          border: 1,
          borderColor: Theme.palette.border,
        }}
      >
        {workout.type === 3 ? (
          <DemandingExerciseChart workoutId={workoutId} />
        ) : (
          <>
            {!statsPending ? (
              <>
                {statistics.attempted_times !== 0 ? (
                  <>
                    {workout?.type !== 3 && (
                      <Stack>
                        <StatisticsRow
                          label={"Your best score"}
                          value={statistics.user_pb + " " + unit}
                        />
                        {isWorkoutPublic && workout?.type !== 3 && (
                          <>
                            <StatisticsRow
                              label={"Leaderboard place"}
                              value={
                                <CenteredRow>
                                  {leaderBoardPlace}
                                  {leaderBoardPlace === 1 ? (
                                    <>
                                      st &nbsp;
                                      <EmojiEventsIcon
                                        sx={{
                                          color: "#d4af37",
                                          fontSize: mobileDevice
                                            ? "1rem"
                                            : "1.2rem",
                                        }}
                                      />
                                    </>
                                  ) : leaderBoardPlace === 2 ? (
                                    <>
                                      nd &nbsp;
                                      <WorkspacePremiumIcon
                                        sx={{
                                          color: "#BCC6CC",
                                          fontSize: mobileDevice
                                            ? "1rem"
                                            : "1.2rem",
                                        }}
                                      />
                                    </>
                                  ) : leaderBoardPlace === 3 ? (
                                    <>
                                      rd &nbsp;
                                      <WorkspacePremiumIcon
                                        sx={{
                                          color: "#49371B",
                                          fontSize: mobileDevice
                                            ? "1rem"
                                            : "1.2rem",
                                        }}
                                      />
                                    </>
                                  ) : (
                                    "th"
                                  )}
                                </CenteredRow>
                              }
                            />
                            <StatisticsRow
                              label={"Distance from best score"}
                              value={
                                statistics.score_distance !== 0
                                  ? statistics.score_distance +
                                    " " +
                                    "(" +
                                    statistics.distance_percentage +
                                    "%)"
                                  : "None"
                              }
                            />
                          </>
                        )}
                        {isWorkoutPublic && workout?.type !== 3 && (
                          <StatisticsRow
                            label={"Overall best score (same gender)"}
                            value={statistics.best_score + " " + unit}
                          />
                        )}
                        {(workout?.type === 2 || workout?.type === 5) && (
                          <StatisticsRow
                            label={"Distance from projected score"}
                            value={
                              userScore?.reps -
                              workout.projected_score +
                              " " +
                              unit
                            }
                          />
                        )}
                        {workout?.type === 1 &&
                          statistics.attempted_times !== 0 && (
                            <StatisticsRow
                              label={"Distance from timecap"}
                              isLast={true}
                              value={
                                statistics.timecap_distance +
                                " " +
                                unit +
                                " (" +
                                statistics.timecap_distance_percentage +
                                "%)"
                              }
                            />
                          )}
                      </Stack>
                    )}
                  </>
                ) : (
                  <>
                    {workout?.type !== 3 && (
                      <NoDataDiv
                        height={"100%"}
                        text={" You have not perform this workout yet."}
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <Loader height={"100%"} />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default WorkoutStatistics;
